import type { SelectLine } from '@/objects';
import type { MapPoint, MapTrack } from '@/objects/generated';
import { normalizeHexColor } from '@/utilities/colorUtils';
import { formatDistance } from '@/utilities/utils';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, toRaw } from 'vue';
import { useEventStoreRef } from './eventStore';
import { TrackDao } from '@/dao';
import { MapTrackData } from '@/components/Map/MapTrackData';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';

export type TrackValues = 'Tracks';

export const useMapstore = defineStore('mapstore', () => {
    const { eventID, initpromise } = useEventStoreRef();

    // Contains all the available tracks for the user in the current event.
    const userAvailableTracks = ref<MapTrack[]>([]);

    // This is the internal cache of the track points for the tracks.
    // TODO: This will not update when the tracks are updated, so we need to make sure to clear this cache when the tracks are updated.
    const trackpoints = ref<Map<number, MapPoint[]>>(new Map<number, MapPoint[]>());

    /**
     * Loader for the relay contestants
     */
    const availableTrackLoader = ref<LoaderWithReset<MapTrack[]>>(
        new LoaderWithReset<MapTrack[]>({
            defaultValue: [],
            loaderFunction: async () => {
                userAvailableTracks.value = (await TrackDao.GetTracksForUser(eventID.value)) ?? [];
                return userAvailableTracks.value;
            },
            waitOnBeforeLoad: initpromise.value,
        }),
    );

    /**
     * All the available tracks for the user in the current event.
     */
    const userAvailableTracksAsSelectline = computed<SelectLine[]>(() => {
        const _alltracks = toRaw(userAvailableTracks.value);

        const tracks: SelectLine[] = [];
        if (_alltracks.length == 0) {
            return tracks;
        }

        _alltracks.forEach((t) => {
            if (t.Name != null && t.Name != '') {
                // We remove the autogenerated length part of the name on the format (xx.xm)
                const matches = t.Name.match(/.*(\([0-9,.]+m\)).*/is);

                // Remove the match parth from the name
                let name = t.Name;
                if (matches != null && matches.length > 1) {
                    name = t.Name.replace(matches[1], '');
                }

                tracks.push({ value: t.ID, text: name, subtext: formatDistance(t.TrackLength), color: normalizeHexColor(t.LineColor) });
            }
        });

        return tracks;
    });

    /**
     * Get a specific track by its ID.
     * @param trackid
     * @returns
     */
    const getMapTrack = async (trackid: number): Promise<MapTrack | null> => {
        const map = userAvailableTracks.value.find((t) => t.ID === trackid);

        if (!map) {
            // If the track is not found in the user available tracks,
            // we try to fetch it from the database.
            userAvailableTracks.value = (await TrackDao.GetTracksForUser(eventID.value, true)) ?? [];
        }

        return map ?? userAvailableTracks.value.find((t) => t.ID === trackid) ?? null;
    };

    /**
     * This is a waitable function that makes sure we have loaded all the contestants for the event
     * @param dataTypeToEnsureIsLoaded -  undused but this will let guibuilder call this (TODO: Should we make an interface for this?)
     */
    const ensureLoaded = async (dataTypeToEnsureIsLoaded: TrackValues[]) => {
        const promises = [];

        if (dataTypeToEnsureIsLoaded.includes('Tracks')) {
            promises.push(availableTrackLoader.value.Load());
        }

        await Promise.all(promises);
    };

    /**
     * Get the track points for a specific track.
     * @param trackid
     * @returns
     */
    const getTrackPointsForTrack = async (trackid: number): Promise<MapPoint[]> => {
        // First we check if we are allowed
        // to access the track points for this track.
        if (!userAvailableTracks.value.some((t) => t.ID === trackid)) {
            console.error(`Track with ID ${trackid} is not available for the user in the current event.`);
            return [];
        }

        if (trackpoints.value.has(trackid)) {
            return trackpoints.value.get(trackid) ?? [];
        }
        const points = await TrackDao.GetMapPointsForTrack(eventID.value, trackid);
        trackpoints.value.set(trackid, points);

        return points;
    };

    /**
     * Return a track with its points.
     * @param trackid
     * @returns
     */
    const getTrackWithPoints = async (trackid: number): Promise<MapTrackData | null> => {
        if (userAvailableTracks.value.length === 0) {
            userAvailableTracks.value = (await TrackDao.GetTracksForUser(eventID.value, true)) ?? [];
        }

        const track = userAvailableTracks.value.find((t) => t.ID === trackid);
        if (!track) {
            console.error(`Track with ID ${trackid} is not available for the user in the current event.`);
            return null;
        }

        const maptrackdata = new MapTrackData(track);
        maptrackdata.MapTrack.Points = await getTrackPointsForTrack(trackid);

        return maptrackdata;
    };

    return {
        /**
         * This is a waitable function that makes sure we have loaded all the tracks for the event
         * @param dataTypeToEnsureIsLoaded -  undused but this will let guibuilder call this (TODO: Should we make an interface for this?)
         */
        EnsureLoaded: ensureLoaded,

        /**
         * A list of all the available tracks for the user in the current event.
         */
        UserAvailableTracks: userAvailableTracks,

        /**
         * A list of all the available tracks for the user in the current event, formatted for select components.
         */
        UserAvailableTracksAsSelectline: userAvailableTracksAsSelectline,

        /**
         * Return a complete track with its points.
         * @returns MapTrackData or null if the track is not available for the user.
         */
        GetMapTrackData: getTrackWithPoints,

        /**
         * Return the MapTrack for a specific track ID.
         * @remarks This is without points
         */
        GetMapTrack: getMapTrack,
    };
});

/**
 * A reactive reference to the common store.
 * @returns
 */
export const useMapstoreRef = () => storeToRefs(useMapstore());
